import {
  careNavigationTeamNavigators,
  careNavigationAccountId,
  careNavigationSelectedMembers,
  careNavigationScopeMembers,
  careNavigationTeams,
  type CareNavigationTeamId,
} from "./careNavigationTeams";
import { CareNavigationRoster } from "./careNavigationRoster";
import { Button } from "~/components/ui/button";
import { CareNavigationCarePlans } from "./CareNavigationCarePlans";
import { buildCareNavigationMetricsCsvRows } from "./careNavigationMetricsExport";
import { exportRowsToCsv } from "./dashboardsExport";
import {
  CareNavigationNavigatorFilter,
  CareNavigationTeamFilter,
  CareNavigationStaffStatusFilter,
} from "./careNavigationNavigatorFilter";
import { CareNavigationReassessmentStatus } from "./careNavigationReassessmentStatus";
import { CarespaceRecordsProvider } from "./carespaceRecords";
import {
  CARE_NAVIGATION_CRITERIA,
  CARE_NAVIGATION_TARGETS,
  DEFAULT_CARE_NAVIGATION_CRITERIA,
  type CareNavigationCriteria,
} from "./careNavigationCriteria";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { CareNavigationCompletionKpis } from "./careNavigationCompletionKpis";
import { CarespaceCountLink } from "./carespaceCountLink";
import { CareNavigationCalendar } from "./careNavigationCalendar";
import { CareNavigationMonthlyCompletion } from "./careNavigationMonthlyCompletion";
import { CareNavigationSummaryTable } from "./careNavigationSummaryTable";
import {
  readDefinitionState,
  summarizeMonthlyCallDurations,
  summarizeCompletionRows,
  summarizeNavigatorAttrition,
  writeDefinitionState,
  type CareNavigationDefinitionState,
  type CompletionChannel,
} from "./careNavigationDefinitionState";
import {
  DASHBOARD_TIMEZONE,
  formatMonthOptionLabel,
  getMonthBounds,
  getMonthOptions,
  todayIsoInTimezone,
} from "./careNavigationShared";
import type { CalendarStatusFilter } from "./careNavigationTypes";
import {
  DashboardSelect,
  DashboardsPageFrame,
  HorizonTabs,
  KpiCard,
  KpiGrid,
} from "./dashboardKit";
import {
  careNavigationCalendarStatusQueryOptions,
  careNavigationDefinitionQueryOptions,
  careNavigationMonthlyCompletionQueryOptions,
  careNavigationRosterQueryOptions,
} from "./dashboardsReactQuery";

const CALL_CRITERIA = CARE_NAVIGATION_CRITERIA.filter(({ kpis }) =>
  (kpis as readonly string[]).includes("call"),
);

export function CareNavigationCriteriaPanel(props: {
  criteria: CareNavigationCriteria;
  onToggle: (id: keyof CareNavigationCriteria) => void;
  onReset: () => void;
}) {
  const changed = CARE_NAVIGATION_CRITERIA.filter(
    ({ id, defaultEnabled }) => props.criteria[id] !== defaultEnabled,
  );
  return (
    <details className="rounded-lg border border-border/70 bg-background p-4">
      <summary className="cursor-pointer text-sm font-semibold">
        Definition criteria {changed.length ? `(${changed.length} changed)` : "(agreed defaults)"}
      </summary>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Each criterion changes only its listed KPIs; CN selection is a separate filter.
        </p>
        <button
          type="button"
          onClick={props.onReset}
          className="rounded-md border px-3 py-1 text-xs"
        >
          Reset to agreed definition
        </button>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {CARE_NAVIGATION_CRITERIA.map(({ id, label, kpis, defaultEnabled }) => {
          const available =
            CALL_CRITERIA.some((criterion) => criterion.id === id) || id === "pendingReassessments";
          return (
            <label
              key={id}
              className="flex items-start gap-2 rounded-md border border-border/60 p-2 text-sm"
            >
              <input
                type="checkbox"
                aria-label={label}
                checked={props.criteria[id]}
                disabled={!available}
                onChange={() => props.onToggle(id)}
              />
              <span>
                {label}{" "}
                {props.criteria[id] !== defaultEnabled ? (
                  <strong className="text-amber-700">Changed</strong>
                ) : null}
                <span className="block text-xs text-muted-foreground">
                  {kpis.join(", ")}
                  {available ? "" : " · available with its KPI rollout"}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </details>
  );
}

export function CareNavigationRebuiltDashboard() {
  const [tab, setTab] = useState("completion");
  return (
    <>
      <DashboardsPageFrame>
        <HorizonTabs
          idPrefix="care-navigation"
          value={tab}
          onChange={setTab}
          options={[
            { value: "completion", label: "Monthly completion" },
            { value: "care-plans", label: "Care plans" },
          ]}
        />
      </DashboardsPageFrame>
      <div role="tabpanel" aria-labelledby={`care-navigation-tab-${tab}`}>
        {tab === "care-plans" ? <CareNavigationCarePlans /> : <CareNavigationMetricsDashboard />}
      </div>
    </>
  );
}

function CareNavigationMetricsDashboard() {
  const today = todayIsoInTimezone("UTC");
  const currentMonth = today.slice(0, 7);
  const monthOptions = useMemo(() => getMonthOptions(18, today), [today]);
  const [definition, setDefinition] = useState<CareNavigationDefinitionState>(() =>
    typeof window === "undefined"
      ? { month: currentMonth, navigators: [], criteria: { ...DEFAULT_CARE_NAVIGATION_CRITERIA } }
      : readDefinitionState(window.location.search, currentMonth),
  );
  const [completionChannel, setCompletionChannel] = useState<CompletionChannel>("calls");
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatusFilter>("all");
  const month = monthOptions.includes(definition.month) ? definition.month : currentMonth;
  const bounds = useMemo(() => getMonthBounds(month, today), [month, today]);
  const timeframe = useMemo(
    () => ({
      windowPreset: "monthly" as const,
      anchorDate: bounds.dateEnd,
      dateStart: bounds.dateStart,
      dateEnd: bounds.dateEnd,
      timezone: DASHBOARD_TIMEZONE,
    }),
    [bounds],
  );

  useEffect(() => {
    const restore = () => setDefinition(readDefinitionState(window.location.search, currentMonth));
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, [currentMonth]);

  const changeDefinition = (next: CareNavigationDefinitionState) => {
    setDefinition(next);
    const search = writeDefinitionState(window.location.search, next, currentMonth);
    window.history.pushState(
      null,
      "",
      `${window.location.pathname}${search}${window.location.hash}`,
    );
  };

  const rosterQuery = useQuery(careNavigationRosterQueryOptions());
  const navigatorOptions = useQuery(
    careNavigationDefinitionQueryOptions({
      anchorDate: timeframe.anchorDate,
      criteria: definition.criteria,
      section: "completion",
    }),
  );
  const detailsQuery = useQuery({
    ...careNavigationDefinitionQueryOptions({
      anchorDate: timeframe.anchorDate,
      criteria: definition.criteria,
      section: "reassessment",
    }),
    enabled: navigatorOptions.isSuccess,
  });
  const response = navigatorOptions.data;
  const billingQuery = useQuery(careNavigationMonthlyCompletionQueryOptions(timeframe.anchorDate));
  const rosterMembers = rosterQuery.data?.members ?? [];
  const scopedMembers = careNavigationScopeMembers(
    rosterMembers,
    definition.team,
    definition.staffStatus,
  );
  const availableNavigators = careNavigationTeamNavigators(
    undefined,
    rosterMembers,
    definition.staffStatus,
  );
  const selectedNavigatorNames = definition.team
    ? careNavigationTeamNavigators(definition.team, rosterMembers, definition.staffStatus)
    : definition.navigators;
  const matchedMembers = careNavigationSelectedMembers(
    rosterMembers,
    definition.team,
    definition.staffStatus,
    definition.navigators,
    rosterQuery.data?.unmatchedNavigatorNames,
  );
  const scopedNavigatorIds = matchedMembers.map((member) => member.navigatorUserId!);
  const scopedNavigatorNames = [
    ...new Set(
      matchedMembers.flatMap((member) => (member.navigatorName ? [member.navigatorName] : [])),
    ),
  ].sort();
  const accountId = (row: { navigatorUserId?: string | null; navigatorName: string | null }) =>
    careNavigationAccountId(row, rosterMembers, rosterQuery.data?.unmatchedNavigatorNames);
  const inScope = (row: { navigatorUserId?: string | null; navigatorName: string | null }) => {
    const id = accountId(row);
    return id !== undefined && scopedNavigatorIds.includes(id);
  };
  const scopeReady = rosterQuery.isSuccess && !rosterQuery.isRefetchError;
  const [exportPending, setExportPending] = useState(false);
  const [exportError, setExportError] = useState("");
  const durationMonths = [
    ...new Set([bounds.dateStart, ...(response?.rows ?? []).map((row) => row.monthStart)]),
  ];
  const durationQueries = useQueries({
    queries: durationMonths.map((monthStart) => ({
      ...careNavigationDefinitionQueryOptions({
        anchorDate:
          monthStart === bounds.dateStart
            ? timeframe.anchorDate
            : getMonthBounds(monthStart.slice(0, 7), today).dateEnd,
        criteria: definition.criteria,
        section: "duration",
        // The SQL aggregate deduplicates calls shared by the selected navigators.
        navigatorUserIds: scopedNavigatorIds,
      }),
      enabled: navigatorOptions.isSuccess && scopeReady && scopedNavigatorNames.length > 0,
    })),
  });
  const currentDurationQuery = durationQueries[0];
  const monthlyCallDurationStatuses = new Map(
    durationMonths.map((monthStart, index) => {
      const query = durationQueries[index];
      const status =
        navigatorOptions.isPending ||
        !scopeReady ||
        (scopedNavigatorNames.length > 0 && query?.isPending)
          ? ("loading" as const)
          : navigatorOptions.isError || query?.isError
            ? ("unavailable" as const)
            : ("available" as const);
      return [monthStart, status] as const;
    }),
  );
  const monthlyCallDurationSummary = summarizeMonthlyCallDurations(
    durationQueries.flatMap((query) =>
      (query.data?.monthlyCallDurations ?? [])
        .filter((row) => row.navigatorName === null || inScope(row))
        .map((row) => ({
          ...row,
          navigatorUserId: row.navigatorName === null ? null : (accountId(row) ?? null),
          navigatorName:
            row.navigatorName === null
              ? null
              : (matchedMembers.find((member) => member.navigatorUserId === accountId(row))
                  ?.navigatorName ?? row.navigatorName),
        })),
    ),
    bounds.dateStart,
    scopedNavigatorIds,
    monthlyCallDurationStatuses,
  );
  const rows = (response?.rows ?? [])
    .filter((row) => scopeReady && inScope(row))
    .map((row) => {
      const detail = detailsQuery.data?.rows.find(
        (detail) =>
          detail.monthStart === row.monthStart &&
          accountId(detail) === accountId(row) &&
          detail.tier === row.tier,
      );
      const durationQuery = durationQueries[durationMonths.indexOf(row.monthStart)];
      const measured = durationQuery?.data?.rows.find(
        (item) =>
          item.monthStart === row.monthStart &&
          accountId(item) === accountId(row) &&
          item.tier === row.tier,
      );
      return {
        ...row,
        navigatorName:
          matchedMembers.find((member) => member.navigatorUserId === accountId(row))
            ?.navigatorName ?? row.navigatorName,
        durationStatus: durationQuery?.isPending
          ? ("loading" as const)
          : durationQuery?.isError
            ? ("unavailable" as const)
            : ("available" as const),
        callDurationMinutes: measured?.callDurationMinutes ?? 0,
        callDurationCount: measured?.callDurationCount ?? 0,
        needsAssessmentDurationMinutes: measured?.needsAssessmentDurationMinutes ?? 0,
        needsAssessmentDurationCount: measured?.needsAssessmentDurationCount ?? 0,
        reassessmentDenominator: detail?.reassessmentDenominator ?? row.reassessmentDenominator,
        reassessmentCount: detail?.reassessmentCount ?? row.reassessmentCount,
        reassessmentInWindowCount:
          detail?.reassessmentInWindowCount ?? row.reassessmentInWindowCount,
        reassessmentUpcomingCount:
          detail?.reassessmentUpcomingCount ?? row.reassessmentUpcomingCount,
        reassessmentOverdueCount: detail?.reassessmentOverdueCount ?? row.reassessmentOverdueCount,
        reassessmentPendingCount: detail?.reassessmentPendingCount ?? row.reassessmentPendingCount,
        recordIds: {
          ...row.recordIds,
          ...Object.fromEntries(
            Object.entries(detail?.recordIds ?? {}).filter(
              ([key]) =>
                key.startsWith("reassessment") ||
                ["inWindow", "upcoming", "overdue", "pending"].includes(key),
            ),
          ),
        },
      };
    });
  const currentRows = rows.filter((row) => row.monthStart === bounds.dateStart);
  const currentRecordIds = (key: string) => [
    ...new Set(currentRows.flatMap((row) => row.recordIds?.[key] ?? [])),
  ];
  const completion = summarizeCompletionRows(rows, bounds.dateStart);
  const navigatorAttrition = summarizeNavigatorAttrition(rows, bounds.dateStart);
  const durationTrendByMonth = new Map(completion.trend);
  for (const [monthStart, duration] of monthlyCallDurationSummary.trend) {
    const base =
      durationTrendByMonth.get(monthStart) ?? summarizeCompletionRows([], monthStart).current;
    durationTrendByMonth.set(monthStart, { ...base, ...duration });
  }
  const durationTrendEntries = Array.from(durationTrendByMonth).sort(([left], [right]) =>
    left.localeCompare(right),
  );
  const completionByNavigator = new Map(completion.navigators);
  const monthlyCallDurationByNavigator = new Map(monthlyCallDurationSummary.navigators);
  const durationNavigatorNames = new Set([
    ...completionByNavigator.keys(),
    ...monthlyCallDurationByNavigator.keys(),
    ...scopedNavigatorNames,
  ]);
  const emptyCompletion = summarizeCompletionRows([], bounds.dateStart).current;
  const durationNavigatorEntries: Array<[string, typeof emptyCompletion]> = Array.from(
    durationNavigatorNames,
  )
    .sort((left, right) => left.localeCompare(right))
    .map((name): [string, typeof emptyCompletion] => [
      name,
      {
        ...(completionByNavigator.get(name) ?? emptyCompletion),
        ...(monthlyCallDurationByNavigator.get(name) ?? {
          ...monthlyCallDurationSummary.current,
          callDurationMinutes: 0,
          callDurationCount: 0,
          callContactCount: 0,
        }),
      },
    ]);

  const changed = CARE_NAVIGATION_CRITERIA.filter(
    ({ id, defaultEnabled }) => definition.criteria[id] !== defaultEnabled,
  );
  const trends = completion.trend;
  const reassessmentTracked = bounds.dateStart >= CARE_NAVIGATION_TARGETS.reassessmentTrackingStart;
  const navigators = availableNavigators;

  const calendarQuery = useQuery({
    ...careNavigationCalendarStatusQueryOptions({
      filters: { navigatorIds: scopedNavigatorIds },
      status: calendarStatus,
      limit: 250,
      ...timeframe,
    }),
    enabled: navigatorOptions.isSuccess && scopeReady && scopedNavigatorNames.length > 0,
  });

  const exportReady =
    scopeReady &&
    scopedNavigatorNames.length > 0 &&
    navigatorOptions.isSuccess &&
    detailsQuery.isSuccess &&
    currentDurationQuery?.isSuccess &&
    billingQuery.isSuccess;
  async function downloadMetrics() {
    if (!exportReady || !scopeReady || exportPending) return;
    setExportPending(true);
    setExportError("");
    try {
      const csvRows = buildCareNavigationMetricsCsvRows({
        monthStart: bounds.dateStart,
        dateEnd: timeframe.anchorDate,
        rows: currentRows,
        billingRows: (billingQuery.data?.rows ?? []).filter(inScope).map((row) => ({
          ...row,
          navigatorName:
            matchedMembers.find((member) => member.navigatorUserId === row.navigatorUserId)
              ?.navigatorName ?? row.navigatorName,
        })),
        durations: durationNavigatorEntries,
        navigators: scopedNavigatorNames,
        criteria: definition.criteria,
      });
      await exportRowsToCsv(csvRows, `care-navigation-metrics-${month}.csv`);
    } catch {
      setExportError("Metrics could not be downloaded. Please try again.");
    } finally {
      setExportPending(false);
    }
  }

  function changeTeam(team: CareNavigationTeamId | undefined) {
    const next = { ...definition, navigators: [] };
    if (team) next.team = team;
    else delete next.team;
    changeDefinition(next);
  }

  function changeNavigators(navigators: string[]) {
    if (
      definition.team &&
      navigators.length === selectedNavigatorNames.length &&
      navigators.every((name) => selectedNavigatorNames.includes(name))
    )
      return;
    const next = { ...definition, navigators };
    delete next.team;
    changeDefinition(next);
  }

  if (!scopeReady) {
    return (
      <DashboardsPageFrame className="space-y-4">
        <h1 className="text-2xl font-semibold">Care Navigation</h1>
        {rosterQuery.isPending ? (
          <p>Loading team directory…</p>
        ) : (
          <>
            <p role="alert">
              The Care Navigation directory is unavailable. Metrics require a current team roster.
            </p>
            <Button variant="outline" onClick={() => void rosterQuery.refetch()}>
              Retry directory
            </Button>
          </>
        )}
      </DashboardsPageFrame>
    );
  }

  const header = (
    <>
      <header className="rounded-lg border border-border/70 bg-background p-5">
        <h1 className="text-2xl font-semibold">Care Navigation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Monthly completion for CareSpaces aligned during the reporting month, with adjustable
          qualifying call metrics.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="grid gap-1 text-xs">
            Month
            <DashboardSelect
              value={month}
              onChange={(event) => changeDefinition({ ...definition, month: event.target.value })}
            >
              {monthOptions.map((option) => (
                <option key={option} value={option}>
                  {formatMonthOptionLabel(option, currentMonth)}
                </option>
              ))}
            </DashboardSelect>
          </label>
          <CareNavigationTeamFilter
            team={definition.team}
            teams={careNavigationTeams(rosterMembers)}
            selected={definition.navigators}
            onChange={changeTeam}
          />
          <CareNavigationStaffStatusFilter
            value={definition.staffStatus ?? "active"}
            onChange={(staffStatus) =>
              changeDefinition({
                ...definition,
                navigators: [],
                staffStatus,
              })
            }
          />
          <CareNavigationNavigatorFilter
            options={navigators}
            selected={selectedNavigatorNames}
            onChange={changeNavigators}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={downloadMetrics}
            disabled={!exportReady || exportPending}
          >
            {exportPending ? "Downloading…" : "Download metrics CSV"}
          </Button>
        </div>
        {exportError ? (
          <p role="alert" className="mt-2 text-sm">
            {exportError}
          </p>
        ) : null}
      </header>

      <CareNavigationRoster roster={rosterQuery.data!} members={scopedMembers} />
    </>
  );
  if (!scopedNavigatorNames.length) {
    return (
      <DashboardsPageFrame className="space-y-6">
        {header}
        <p role="status">No staff with a matched Carespaces account in this selection.</p>
      </DashboardsPageFrame>
    );
  }

  return (
    <CarespaceRecordsProvider
      filePrefix="care-navigation"
      records={rows.flatMap((row) => row.records ?? [])}
    >
      <DashboardsPageFrame className="space-y-6">
        {header}
        <CareNavigationCompletionKpis
          sourceRows={currentRows}
          counts={completion.current}
          loading={navigatorOptions.isPending}
          unavailable={navigatorOptions.isError || response?.cache === "STALE"}
          emailFinalized={Boolean(
            response?.emailFinalized ||
            (currentRows.length > 0 && currentRows.every((row) => row.emailFinalized)),
          )}
        />
        <p className="text-xs text-muted-foreground">
          Rates are weighted by CareSpaces aligned at any point in each reporting month. Navigators
          without assigned CareSpaces contribute zero to the denominator; new hires with assigned
          caseload remain included.
        </p>

        {currentRows.some((row) => (row.unbilledEmailCount ?? 0) > 0) ? (
          <p
            role="status"
            className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
          >
            {currentRows.reduce((sum, row) => sum + (row.unbilledEmailCount ?? 0), 0)} aligned
            CareSpaces have delivered emails without recorded monthly billing. Email and total
            billed completion may be incomplete. Delivery alone does not establish billing
            eligibility.
          </p>
        ) : null}

        <CareNavigationMonthlyCompletion
          anchorDate={timeframe.anchorDate}
          monthStart={bounds.dateStart}
          navigatorUserIds={scopedNavigatorIds}
        />

        <CareNavigationCriteriaPanel
          criteria={definition.criteria}
          onToggle={(id) =>
            changeDefinition({
              ...definition,
              criteria: { ...definition.criteria, [id]: !definition.criteria[id] },
            })
          }
          onReset={() =>
            changeDefinition({
              ...definition,
              criteria: { ...DEFAULT_CARE_NAVIGATION_CRITERIA },
            })
          }
        />

        <section className="space-y-3" aria-label="Qualifying completion">
          <h2 className="text-lg font-semibold">Qualifying completion</h2>
          <div className="flex gap-2" role="group" aria-label="Completion channel">
            {(
              [
                ["calls", "Calls"],
                ["emails", "Email"],
                ["combined", "Email + calls"],
              ] as const
            ).map(([channel, label]) => (
              <button
                key={channel}
                type="button"
                aria-pressed={completionChannel === channel}
                className={`rounded-md border px-3 py-1 text-sm ${completionChannel === channel ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                onClick={() => setCompletionChannel(channel)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Email + calls counts each Carespace once when either channel is completed. Email counts
            remain preliminary until reconciliation.
          </p>
          {navigatorOptions.isError ? (
            <p role="alert">Completion data is temporarily unavailable.</p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Active:{" "}
            {CALL_CRITERIA.filter(({ id }) => definition.criteria[id])
              .map(({ label }) => label)
              .join(", ") || "none"}
            .
            {changed.length
              ? ` Changed from agreed definition: ${changed.map(({ label }) => label).join(", ")}.`
              : " Agreed definition."}
          </p>
          {response?.snapshotDate ? (
            <p className="text-xs text-muted-foreground">
              Call-only completion preserved at end of day {response.snapshotDate}.
              {response.emailFinalized
                ? " Email counts finalized separately after rollover."
                : " Email counts remain preliminary while billing is reconciled after rollover."}
            </p>
          ) : month !== currentMonth ? (
            <p className="text-xs text-amber-700">
              Historical figures are recalculated; no end-of-day snapshot applies to these criteria.
            </p>
          ) : null}
          <div className="overflow-auto rounded-lg border border-border/70 p-4">
            <h3 className="font-medium">Month over month</h3>
            <CareNavigationSummaryTable
              metric="calls"
              channel={completionChannel}
              breakdown="month"
              criteria={definition.criteria}
              entries={trends}
              sourceRows={rows}
            />
          </div>
          <div className="overflow-auto rounded-lg border border-border/70 p-4">
            <h3 className="font-medium">By care navigator</h3>
            <CareNavigationSummaryTable
              metric="calls"
              channel={completionChannel}
              breakdown="navigator"
              criteria={definition.criteria}
              entries={completion.navigators}
              sourceRows={currentRows}
              attrition={navigatorAttrition}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Attrition is offboarded assigned carespaces MTD divided by all currently assigned
              carespaces, not a headline KPI or call-completion criterion.
            </p>
          </div>
        </section>

        <section
          className="space-y-3 rounded-lg border border-border/70 bg-background p-4"
          aria-label="Average monthly call length"
        >
          <h2 className="text-lg font-semibold">Average monthly call length</h2>
          <KpiGrid>
            <KpiCard
              label="Average monthly call"
              value={
                monthlyCallDurationSummary.current.durationStatus === "loading"
                  ? "…"
                  : monthlyCallDurationSummary.current.durationStatus === "unavailable" ||
                      !monthlyCallDurationSummary.current.callDurationCount
                    ? "—"
                    : `${(monthlyCallDurationSummary.current.callDurationMinutes / monthlyCallDurationSummary.current.callDurationCount).toFixed(2)} min`
              }
              footnote={
                monthlyCallDurationSummary.current.durationStatus === "loading"
                  ? "Loading monthly call lengths"
                  : monthlyCallDurationSummary.current.durationStatus === "unavailable"
                    ? "Monthly call lengths temporarily unavailable"
                    : `${monthlyCallDurationSummary.current.callDurationCount} Dialpad monthly-call dispositions · >${CARE_NAVIGATION_TARGETS.averageCallMinutes} min average-length goal`
              }
            />
            <KpiCard
              label="Average needs assessment call"
              value={
                navigatorOptions.isPending || currentDurationQuery?.isPending
                  ? "…"
                  : navigatorOptions.isError ||
                      currentDurationQuery?.isError ||
                      !completion.current.needsAssessmentDurationCount
                    ? "—"
                    : `${(completion.current.needsAssessmentDurationMinutes / completion.current.needsAssessmentDurationCount).toFixed(1)} min`
              }
              footnote={
                navigatorOptions.isPending || currentDurationQuery?.isPending
                  ? "Loading needs assessment call lengths"
                  : navigatorOptions.isError || currentDurationQuery?.isError
                    ? "Needs assessment call lengths temporarily unavailable"
                    : `${completion.current.needsAssessmentDurationCount} matched needs assessment calls · annual assessments excluded`
              }
            />
          </KpiGrid>
          {currentDurationQuery?.data?.cache === "STALE" ||
          currentDurationQuery?.data?.durationStaleMonths?.includes(bounds.dateStart) ? (
            <p role="status">
              Showing previously measured call lengths while refresh is unavailable.
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            This average uses Dialpad calls labeled “Monthly call” or “Planned monthly call.” It
            credits the handler when the handler maps to a care navigator, then falls back to the
            current Carespace assignment. Handler-resolved calls count even without a Carespace
            match. A call counts once per care navigator and once in the team average. Needs
            assessment length remains a separate matched-contact measure; the minimum-duration
            toggle still uses that strict encounter match.
          </p>
          <div className="overflow-auto rounded-lg border border-border/70 p-4">
            <h3 className="font-medium">Month over month</h3>
            <CareNavigationSummaryTable
              metric="duration"
              loading={monthlyCallDurationSummary.current.durationStatus === "loading"}
              unavailable={monthlyCallDurationSummary.current.durationStatus === "unavailable"}
              breakdown="month"
              criteria={definition.criteria}
              entries={durationTrendEntries}
              sourceRows={rows}
            />
          </div>
          <div className="overflow-auto rounded-lg border border-border/70 p-4">
            <h3 className="font-medium">By care navigator</h3>
            <CareNavigationSummaryTable
              metric="duration"
              loading={monthlyCallDurationSummary.current.durationStatus === "loading"}
              unavailable={monthlyCallDurationSummary.current.durationStatus === "unavailable"}
              breakdown="navigator"
              criteria={definition.criteria}
              entries={durationNavigatorEntries}
              sourceRows={currentRows}
            />
          </div>
        </section>

        <section
          className="space-y-3 rounded-lg border border-border/70 bg-background p-4"
          aria-label="Care plan completion"
        >
          <h2 className="text-lg font-semibold">Care plan completion</h2>
          <KpiGrid>
            <KpiCard
              label="Updated care plan"
              value={
                navigatorOptions.isPending
                  ? "…"
                  : navigatorOptions.isError || !completion.current.carePlanEligible
                    ? "—"
                    : `${((completion.current.carePlanUpdated / completion.current.carePlanEligible) * 100).toFixed(1)}%`
              }
              footnote={
                <>
                  <CarespaceCountLink
                    count={response ? completion.current.carePlanUpdated : null}
                    recordIds={currentRecordIds("carePlanUpdated")}
                    label="Updated care plans"
                  />{" "}
                  /{" "}
                  <CarespaceCountLink
                    count={response ? completion.current.carePlanEligible : null}
                    recordIds={currentRecordIds("carePlanEligible")}
                    label="Eligible for care-plan completion"
                  />{" "}
                  eligible Carespaces · &gt;
                  {Math.round(CARE_NAVIGATION_TARGETS.carePlanCompletion * 100)}% target
                </>
              }
            />
          </KpiGrid>
          <p className="text-xs text-muted-foreground">
            Intervention and goal additions, updates, and completions. Comments do not count.
            Carespaces with neither a qualifying encounter/Low-tier group nor a CP update do not
            count against completion.
            {changed.length ? " Definition differs from agreed defaults." : " Agreed definition."}
          </p>
          <div className="overflow-auto">
            <CareNavigationSummaryTable
              metric="carePlan"
              breakdown="navigator"
              criteria={definition.criteria}
              entries={completion.navigators}
              sourceRows={currentRows}
            />
          </div>
          <div className="overflow-auto">
            <h3 className="font-medium">Month-over-month care plan completion</h3>
            <CareNavigationSummaryTable
              metric="carePlan"
              breakdown="month"
              criteria={definition.criteria}
              entries={trends}
              sourceRows={rows}
            />
          </div>
        </section>

        <section
          className="space-y-3 rounded-lg border border-border/70 bg-background p-4"
          aria-label="Annual reassessments"
        >
          <h2 className="text-lg font-semibold">Annual reassessments</h2>
          <KpiGrid>
            <KpiCard
              label="Completed inside annual window"
              value={
                !reassessmentTracked ||
                detailsQuery.isPending ||
                detailsQuery.isError ||
                !completion.current.reassessmentDenominator
                  ? "—"
                  : `${((completion.current.reassessmentCount / completion.current.reassessmentDenominator) * 100).toFixed(1)}%`
              }
              footnote={
                <>
                  <CarespaceCountLink
                    count={detailsQuery.data ? completion.current.reassessmentCount : null}
                    recordIds={currentRecordIds("reassessmentCount")}
                    label="Completed annual reassessments"
                  />{" "}
                  /{" "}
                  <CarespaceCountLink
                    count={detailsQuery.data ? completion.current.reassessmentDenominator : null}
                    recordIds={currentRecordIds("reassessmentDenominator")}
                    label="Due annual reassessments"
                  />{" "}
                  due this month through {timeframe.dateEnd} ·{" "}
                  {Math.round(CARE_NAVIGATION_TARGETS.reassessmentCompletion * 100)}% target
                </>
              }
            />
          </KpiGrid>
          <p className="text-xs text-muted-foreground">
            {reassessmentTracked
              ? "Homepage window: days 306–425 after the last approved assessment."
              : "Tracking begins July 2026."}
            {definition.criteria.pendingReassessments
              ? " Pending and submitted PAFs count."
              : " Only approved PAFs count."}
            {changed.length ? " Definition differs from agreed defaults." : " Agreed definition."}
          </p>
          <CareNavigationReassessmentStatus rows={currentRows} available={!!detailsQuery.data} />
          <div className="overflow-auto">
            <h3 className="font-medium">Monthly due cohorts</h3>
            <CareNavigationSummaryTable
              metric="reassessments"
              loading={detailsQuery.isPending}
              unavailable={detailsQuery.isError}
              breakdown="month"
              criteria={definition.criteria}
              sourceRows={rows}
              entries={trends.filter(
                ([monthStart]) => monthStart >= CARE_NAVIGATION_TARGETS.reassessmentTrackingStart,
              )}
            />
          </div>
          <div className="overflow-auto">
            <h3 className="font-medium">By care navigator</h3>
            <CareNavigationSummaryTable
              metric="reassessments"
              loading={detailsQuery.isPending}
              unavailable={detailsQuery.isError}
              breakdown="navigator"
              criteria={definition.criteria}
              entries={completion.navigators}
              sourceRows={currentRows}
            />
          </div>
        </section>

        <section
          aria-label="Needs scheduling"
          className="rounded-lg border border-border/70 bg-background p-4"
        >
          <CareNavigationCalendar
            status={calendarStatus}
            exportRequest={{
              filters: { navigatorIds: scopedNavigatorIds },
              status: calendarStatus,
              ...timeframe,
            }}
            onStatusChange={setCalendarStatus}
            rows={scopedNavigatorNames.length ? (calendarQuery.data?.rows ?? []) : []}
            summary={scopedNavigatorNames.length ? (calendarQuery.data?.summary ?? {}) : {}}
            loading={calendarQuery.isLoading}
            {...(calendarQuery.error ? { error: "Calendar status is unavailable." } : {})}
          />
        </section>
      </DashboardsPageFrame>
    </CarespaceRecordsProvider>
  );
}
